"use client";

import { Rocket, Settings, Zap } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
} from "@/components/ui/motion";
import { useIsMobile } from "@/hooks/use-mobile";

const steps = [
  {
    number: "01",
    icon: Zap,
    title: "Choose a trigger",
    description:
      "Start with a webhook, schedule, or event. Your automation kicks off exactly when you need it.",
  },
  {
    number: "02",
    icon: Settings,
    title: "Add actions",
    description:
      "Connect to any API, transform data, add conditions. Build your logic visually.",
  },
  {
    number: "03",
    icon: Rocket,
    title: "Deploy and monitor",
    description:
      "Push to production with one click. Watch every run in real-time with full logs.",
  },
];

const runSteps = [
  { label: "Trigger received", duration: 600 },
  { label: "Processing data", duration: 800 },
  { label: "Executing actions", duration: 700 },
  { label: "Complete", duration: 400 },
];

export function HowItWorks() {
  const [activeStep, setActiveStep] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isRunning) {
      const timer = setTimeout(() => {
        if (activeStep < runSteps.length - 1) {
          setActiveStep(activeStep + 1);
        } else {
          setIsRunning(false);
          setTimeout(() => setActiveStep(0), 1500);
        }
      }, runSteps[activeStep].duration);
      return () => clearTimeout(timer);
    }
  }, [activeStep, isRunning]);

  const startDemo = () => {
    setActiveStep(0);
    setIsRunning(true);
  };

  useEffect(() => {
    const timeout = setTimeout(startDemo, 2000);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <section className="py-16 lg:py-24 section-base relative">
      {/* Background gradient orb */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <FadeInUp className="max-w-2xl mx-auto text-center mb-14">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4 tracking-tight">
            From idea to <span className="gradient-text">automation</span> in
            minutes
          </h2>
          <p className="text-lg text-muted-foreground/90">
            Three steps. No complex setup.
          </p>
        </FadeInUp>

        {/* Steps */}
        <StaggerContainer className="grid md:grid-cols-3 gap-8 lg:gap-12 mb-14 max-w-4xl mx-auto">
          {steps.map((step, index) => (
            <StaggerItem key={index}>
              <motion.div
                className="relative text-center"
                whileHover={isMobile ? {} : { y: -4 }}
              >
                {/* Connector */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-px">
                    <div className="h-full bg-gradient-to-r from-primary/50 to-transparent" />
                  </div>
                )}

                <motion.div
                  className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 mb-5 shadow-lg shadow-primary/10 group-hover:shadow-primary/20 transition-shadow"
                  whileHover={{ scale: 1.08 }}
                >
                  <step.icon className="w-6 h-6 text-primary" />
                </motion.div>
                <p className="text-xs font-bold text-primary mb-2 font-mono tracking-wider">
                  {step.number}
                </p>
                <h3 className="text-base font-semibold text-foreground mb-2 tracking-tight">
                  {step.title}
                </h3>
                <p className="text-sm text-muted-foreground/90 leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
}
